/* eslint-disable @typescript-eslint/no-explicit-any */
export interface ShimmedEnv {
  fkUA?: string | undefined
  platform?: string | undefined
  locale?: string | undefined
  isLoggedIn?: boolean | undefined
  abConfig?: any | undefined
  SN?: string | undefined
  SC?: string | undefined
  secureToken?: string | undefined
  appVersion?: string | number | undefined
  omnitureVisitorId?: string | number | undefined
  [key: string]: any
}
// eslint-disable-next-line @typescript-eslint/consistent-indexed-object-style
export interface AppWindow extends Window {
  [index: string]: any | undefined
}

export interface AppPlatform {
  isAndroid: boolean
  isMSite: boolean
  isIOS: boolean
  isDesktop: boolean
  localStorageAvailable: boolean
  sessionStorageAvailable: boolean
  isWindows: boolean
  current: string
  envShim: ShimmedEnv
}
export interface IosInterface {
  method: string
  args: any
  onSuccess: (P?: any) => void
  onFailure: (P?: any) => void
}
export interface RichViewBridge {
  invoke: (method: string, params?: any) => Promise<any>
  hasMethod: (method: string) => boolean
  hasAllMethods: (...methods: string[]) => boolean
  exists: boolean
}

export const isMobile = (): boolean => {
  return /Mobi|Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent,
  )
}

function getUID(): string {
  return `${Date.now()}${(Math.random() * 1e5).toFixed()}`
}

export const applicationPlatform: AppPlatform = ((): AppPlatform => {
  const $W: AppWindow = window
  const hasEnvParams = Boolean($W.appEnvironment)
  // copied as is from MDN
  function storageAvailable(type: string): boolean {
    let storage: any
    try {
      storage = $W[type]
      const x = '__storage_test__'
      storage.setItem(x, x)
      storage.removeItem(x)
      return true
    } catch (e) {
      return (
        e instanceof DOMException &&
        // everything except Firefox
        (e.code === 22 ||
          // Firefox
          e.code === 1014 ||
          // test name field too, because code might not be present
          // everything except Firefox
          e.name === 'QuotaExceededError' ||
          // Firefox
          e.name === 'NS_ERROR_DOM_QUOTA_REACHED') &&
        // acknowledge QuotaExceededError only if there's something already stored
        Boolean(storage) &&
        storage.length !== 0
      )
    }
  }
  let shimmedEnvParams: ShimmedEnv = {}
  if (hasEnvParams) {
    shimmedEnvParams = $W.appEnvironment ?? shimmedEnvParams
  }
  const currentPlatform: string = shimmedEnvParams.platform ?? ''
  const UAString: string = shimmedEnvParams.fkUA ?? $W.navigator.userAgent ?? ''
  const isAndroid: boolean =
    // rome-ignore lint/complexity/useSimplifiedLogicExpression: <explanation>
    Boolean($W.Android) ||
    currentPlatform === 'android' ||
    !!UAString.match(/Android\/Mobile/i) ||
    false
  const isIOS: boolean =
    // rome-ignore lint/complexity/useSimplifiedLogicExpression: <explanation>
    currentPlatform === 'ios' || !!UAString.match(/fk_ios_app FKUA/i) || false
  const isWindows: boolean =
    // rome-ignore lint/complexity/useSimplifiedLogicExpression: <explanation>
    currentPlatform === 'windows' || !!UAString.match(/fk_windows_app/i) || false
  const isMSite: boolean =
    // rome-ignore lint/complexity/useSimplifiedLogicExpression: <explanation>
    currentPlatform === 'web' || (!isAndroid && !isIOS && !isWindows) || false
  const isDesktop = !isMobile()

  const fkUA: string = shimmedEnvParams.fkUA ?? ''
  if (!fkUA || (!fkUA.toLowerCase().includes('fkua') && currentPlatform === 'web')) {
    shimmedEnvParams.fkUA = `${UAString} FKUA/msite/0.0.1/msite/Mobile`
  }

  const applicationPlatform: AppPlatform = {
    isMSite,
    isAndroid,
    isIOS,
    isWindows,
    isDesktop,
    current: 'unknown',
    envShim: shimmedEnvParams,
    localStorageAvailable: storageAvailable('localStorage'),
    sessionStorageAvailable: storageAvailable('sessionStorage'),
  }
  switch (true) {
    case applicationPlatform.isAndroid:
      applicationPlatform.current = 'android'
      break
    case applicationPlatform.isIOS:
      applicationPlatform.current = 'ios'
      break
    case applicationPlatform.isWindows:
      applicationPlatform.current = 'windows'
      break
    case applicationPlatform.isMSite:
      applicationPlatform.current = 'web'
      break
    default:
      break
  }

  return applicationPlatform
})()
;((globalEnv: AppWindow, platform: AppPlatform) => {
  if (platform.isIOS) {
    const addFrame = (src: string): { frame: HTMLElement | undefined; id: string | undefined } => {
      let id = ''
      try {
        const frame = document.createElement('IFRAME')
        const uuid = getUID()
        id = `${uuid}-frame`
        frame.style.width = 'auto'
        frame.style.height = '0px'
        frame.style.opacity = '0'
        frame.style.zIndex = '-999'
        frame.setAttribute('id', `${id}`)
        frame.setAttribute('src', src)
        document.documentElement.appendChild(frame)
        return { frame, id }
      } catch (error: unknown) {
        //
        console.error(error)
        return { frame: undefined, id: '' }
      }
    }
    const constructAppURI = (
      method: string,
      args: any,
      onSuccess: (_arg: any) => void,
      onFailure: () => void,
    ): string => {
      const appInterfaceUrlProtocol = 'fknative://'
      const options: IosInterface = Object.create(null)
      options.method = method
      options.args = args
      options.onSuccess = onSuccess
      options.onFailure = onFailure
      return `${appInterfaceUrlProtocol}${encodeURIComponent(JSON.stringify(options))}`
    }
    globalEnv.NativeInterface = function (
      method: string,
      args: any,
      onSuccess: () => void,
      onFailure: () => void,
    ): void {
      const uri: string = constructAppURI(method, args, onSuccess, onFailure)
      console.log(
        `calling ${uri} with successCB as ${onSuccess.toString()} and failure cb as ${onFailure.toString()}`,
      )
      const { frame: iFrame } = addFrame(uri)
      iFrame?.parentNode?.removeChild(iFrame)
    }
  } else if (platform.isAndroid) {
    // rome-ignore lint/complexity/useSimplifiedLogicExpression: <explanation>
    globalEnv.NativeInterface = globalEnv.Android ?? false
    globalEnv.Android?.setRichView?.(true)
  }
})(window, { ...applicationPlatform })

export const NativeInterface: RichViewBridge = ((
  globalEnv: AppWindow,
  platform: AppPlatform,
): RichViewBridge => {
  let rvNativeInterface: any | undefined
  let hasNativeInterface: boolean | undefined = false

  if (platform.isAndroid) {
    rvNativeInterface = globalEnv.NativeInterface ?? globalEnv.Android
  } else if (platform.isIOS) {
    rvNativeInterface = globalEnv.NativeInterface ?? globalEnv.ios
  } else if (platform.isWindows) {
    try {
      const notifier: any | undefined = globalEnv.external
      if (Boolean(notifier)) {
        const { notify }: { notify: any | undefined | unknown } = notifier
        notify?.('')
        rvNativeInterface = notify
      }
    } catch (error: unknown) {
      rvNativeInterface = undefined
    }
  } else {
    rvNativeInterface = undefined
  }
  hasNativeInterface = Boolean(rvNativeInterface)

  const executeBridgeCall = (
    resolve: (param?: any) => any,
    reject: (param?: any) => any,
    method: string,
    params?: any | undefined,
  ): void => {
    const genericRejection = `Callee ${method} on NativeInterface of ${platform.current} raised an exception`
    const unsupportedBridgeException = `Operating currently on ${platform.current} platform. This doesn't have support for bridges with NativeInterface`
    const uuid = getUID()
    if (platform.isIOS || platform.isWindows) {
      const onSuccessBridgeCB = `${platform.current || 'iOSOrWindows'}BridgeCBFor${
        method || 'unknownMethod'
      }${uuid}`
      const onFailureBridgeCB = `OnFailureOf${onSuccessBridgeCB}`
      console.log(`setting up cb for success with name ${onSuccessBridgeCB}`)

      globalEnv[onSuccessBridgeCB] = ((
        fnName: string,
        fnName2: string,
        resolve: (param?: any) => any,
      ): ((_arg: any) => void) => {
        return function (data: any) {
          let result = data
          try {
            if (Boolean(data) && typeof data === 'string' && data.startsWith('{'))
              result = JSON.parse(data)
          } catch (error: unknown) {
            console.error(error)
          }
          resolve(result)
          try {
            console.log(`deleting ${fnName}`)
            // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
            delete globalEnv[fnName]
            console.log(`deleting ${fnName2}`)
            // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
            delete globalEnv[fnName2]
          } catch (error) {
            console.error(error)
            globalEnv[fnName] = undefined
            globalEnv[fnName2] = undefined
          }
        }
      })(onSuccessBridgeCB, onFailureBridgeCB, resolve)
      console.log(`setting up cb for failure with name ${onFailureBridgeCB}`)
      globalEnv[onFailureBridgeCB] = ((
        fnName: string,
        reject: (param?: any) => any,
        message: string,
      ): (() => void) => {
        return function () {
          try {
            reject(`${message}`)
            // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
            delete globalEnv[fnName]
          } catch (error) {
            console.error(error)
            globalEnv[fnName] = undefined
          }
        }
      })(onFailureBridgeCB, reject, genericRejection)

      if (platform.isIOS) {
        const args = [method, params, onSuccessBridgeCB, onFailureBridgeCB]
        try {
          rvNativeInterface(...args)
        } catch (error) {
          if (error instanceof Error) console.error(`${String(error?.message)} - ${method}`)
          reject(error)
        }
      } else {
        const args = JSON.stringify({
          CommandType: method,
          ParamExtra: params,
          TargetMethod: onSuccessBridgeCB,
        })
        rvNativeInterface(args)
      }
    } else if (platform.isAndroid) {
      const args = params
      let result: any | undefined
      try {
        if (
          !Boolean(rvNativeInterface[method]) ||
          typeof rvNativeInterface[method] !== 'function'
        ) {
          throw new Error(`Unsupported method ${method} on platform ${platform.current}`)
        }
        let data
        if (Array.isArray(params)) {
          data = rvNativeInterface[method](...args)
        } else if (Boolean(params)) {
          data = rvNativeInterface[method](args)
        } else {
          data = rvNativeInterface[method]()
        }

        result = data
        if (Boolean(data) && typeof data === 'string' && data.startsWith('{'))
          result = JSON.parse(data)
        resolve(result)
      } catch (error) {
        console.log(`${method} returned an error on platform ${platform.current}` || '')
        console.error(error)
        // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
        if (error instanceof Error) reject(error?.message || error)
      }
    } else {
      reject(unsupportedBridgeException)
    }
  }

  const invoke = async (method: string, params?: any | undefined): Promise<any> => {
    return await new Promise((resolve, reject) => {
      try {
        if (!Boolean(method)) {
          throw new Error('Bridge calls need a method name to be invoked')
        }
        if (hasNativeInterface === false) {
          throw new Error(`Bridge not defined for platform ${platform.current}`)
        }
      } catch (error: unknown) {
        // reject
        console.error(error)
        if (error instanceof Error) reject(error?.message ? error.message : error)
      }
      executeBridgeCall(resolve, reject, method, params)
    })
  }

  const hasMethod = (method: string): boolean => {
    if (hasNativeInterface && typeof rvNativeInterface[method] === 'function') {
      return true
    }

    return false
  }

  const hasAllMethods = (...methods: string[]): boolean => {
    for (let i = 0; i < methods.length; i++) {
      if (!hasMethod(methods[i])) {
        return false
      }
    }

    return true
  }

  return {
    invoke,
    hasMethod,
    hasAllMethods,
    exists: hasNativeInterface,
  }
})(window, { ...applicationPlatform })

export default applicationPlatform
